/**
 * 用户中心路由配置 - 17页面扁平化架构
 * @description Phase 1: 17页面核心架构搭建，实现"页面直达"设计理念
 * @version 2.0 - 17页面扁平化重构版本
 * @author Phase 1 Core Architecture Team
 */
import type { RouteRecordRaw } from 'vue-router'
import { UserRole } from '@/types/router'

import { userFinanceRoutes } from './user/userFinanceRoutes'
// 导入拆分后的子路由模块
import { userIdentityRoutes } from './user/userIdentityRoutes'
import { userModelRoutes } from './user/userModelRoutes'
import { userTaskRoutes } from './user/userTaskRoutes'

/**
 * 17页面路由配置
 * 架构设计：
 * - 1个用户中心首页
 * - 3个个人账号管理页面
 * - 2个身份认证管理页面（保留4标签页内部结构）
 * - 3个模型业务管理页面
 * - 3个任务业务管理页面
 * - 4个财务业务管理页面
 * - 1个系统集成测试页面
 */
const userRoutes: RouteRecordRaw[] = [
  {
    path: '/user',
    component: () => import('@/components/layout/LayoutUserCenter'),
    meta: {
      title: '用户中心',
      requiresAuth: true,
      icon: 'User',
      order: 4,
      breadcrumb: true,
    },
    children: [
      // ================================
      // 1. 用户中心首页 (1页面)
      // ================================
      {
        path: '',
        name: 'UserCenterHome',
        component: () => import('@/views/userCenter/UserHome.vue'),
        meta: {
          title: '用户中心',
          requiresAuth: true,
          roles: [UserRole.USER, UserRole.DESIGNER, UserRole.MANUFACTURER, UserRole.ADMIN],
          icon: 'House',
          order: 1,
          breadcrumb: false,
          cache: true,
          description: '多身份统一仪表板和快速操作中心',
        },
      },

      // ================================
      // 2-4. 个人账号管理 (3页面)
      // ================================
      {
        path: 'profile',
        name: 'UserProfile',
        component: () => import('@/views/userCenter/UserHome.vue'),
        meta: {
          title: '个人信息',
          requiresAuth: true,
          roles: [UserRole.USER, UserRole.DESIGNER, UserRole.MANUFACTURER, UserRole.ADMIN],
          icon: 'User',
          order: 2,
          breadcrumb: true,
          cache: false,
          description: '个人基本信息管理，头像、昵称、联系方式等',
        },
      },
      {
        path: 'settings',
        name: 'UserSettings',
        component: () => import('@/views/userCenter/AccountSettings.vue'),
        meta: {
          title: '账号设置',
          requiresAuth: true,
          roles: [UserRole.USER, UserRole.DESIGNER, UserRole.MANUFACTURER, UserRole.ADMIN],
          icon: 'Setting',
          order: 3,
          breadcrumb: true,
          cache: false,
          description: '账号安全设置、隐私配置、系统偏好等',
        },
      },
      {
        path: 'messages',
        name: 'UserMessageCenter',
        component: () => import('@/views/userCenter/MessageCenter.vue'),
        meta: {
          title: '消息中心',
          requiresAuth: true,
          roles: [UserRole.USER, UserRole.DESIGNER, UserRole.MANUFACTURER, UserRole.ADMIN],
          icon: 'ChatLineSquare',
          order: 4,
          breadcrumb: true,
          cache: false,
          description: '统一消息管理，系统通知、私信、任务消息等',
        },
      },

      // ================================
      // 5-8. 身份认证管理 (重构版 - 页面拆分架构)
      // ================================
      ...userIdentityRoutes,

      // ================================
      // 7-9. 模型业务管理 (3页面)
      // ================================
      ...userModelRoutes,

      // ================================
      // 10-12. 任务业务管理 (3页面)
      // ================================
      ...userTaskRoutes,

      // ================================
      // 13-16. 财务业务管理 (4页面)
      // ================================
      ...userFinanceRoutes,

      // ================================
      // 17. 系统集成测试页面 (1页面)
      // ================================
      {
        path: 'integration-test',
        name: 'UserCenterIntegrationTest',
        component: () => import('@/views/userCenter/UserHome.vue'),
        meta: {
          title: '功能测试',
          requiresAuth: true,
          roles: [UserRole.USER, UserRole.DESIGNER, UserRole.MANUFACTURER, UserRole.ADMIN],
          icon: 'DataAnalysis',
          order: 20,
          breadcrumb: true,
          cache: false,
          description: '17页面架构集成测试，验证路由、组件和功能完整性',
        },
      },
    ],
  },
]

export default userRoutes
